
import React from 'react';
import { Link } from 'react-router-dom';
import { IMAGE_PATHS } from '@/utils/imagePaths';
import { RemoteImage } from '@/components/common/ResponsiveImage';
import { supabaseImgSrc, supabaseSrcSet } from '@/lib/images';

const EV_PATH = "32f09723-a950-4fbc-95ab-ec37a267ef6f.png" as const;
const GEN_PATH = "8e0f4711-0322-4b4b-8734-c21cf5dd937d.png" as const;

const FeaturedServices = () => {
  return (
    <section className="section bg-white py-8 md:py-10">
      <div className="container-custom">
        <div className="text-center mb-6">
          <h2 className="mb-2">Featured Services</h2>
          <p className="max-w-2xl mx-auto text-navy/80 text-sm md:text-base">
            Discover our specialized electrical services designed to enhance safety, efficiency, and convenience for your home or business.
          </p>
        </div>
        
        {/* EV Charging */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 items-center">
          <div className="rounded-lg overflow-hidden shadow-md h-[250px] md:h-[300px] order-1 md:order-2">
            <RemoteImage
              src={supabaseImgSrc(IMAGE_PATHS[EV_PATH], { width: 960, format: "webp" })}
              srcSet={supabaseSrcSet(IMAGE_PATHS[EV_PATH], [320, 640, 960], "webp")}
              sizes="(max-width: 768px) 100vw, 50vw"
              width={960}
              height={640}
              alt="Level 2 EV charging station installation in residential garage by Elevation Electrical, LLC licensed electricians serving Lake Helen and Central Florida homeowners"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="order-2 md:order-1">
            <h3 className="text-xl font-bold mb-3">EV Charging Stations</h3>
            <p className="mb-3 text-sm md:text-base">
              As electric vehicles become increasingly popular, having a convenient charging solution at home or business is essential. Elevation Electrical specializes in the installation of various EV charging options to suit your specific needs.
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm md:text-base">Level 1 & Level 2 charger installations</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm md:text-base">Dedicated EV circuits and panel upgrades</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm md:text-base">Commercial charging solutions for businesses</span>
              </li>
            </ul>
            <Link to="/services/ev-charging" className="btn-primary">
              Explore EV charger installation
            </Link>
          </div>
        </div>
        
        {/* Generator Solutions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center mt-10">
          <div className="rounded-lg overflow-hidden shadow-md h-[250px] md:h-[300px]">
            <RemoteImage
              src={supabaseImgSrc(IMAGE_PATHS[GEN_PATH], { width: 960, format: "webp" })}
              srcSet={supabaseSrcSet(IMAGE_PATHS[GEN_PATH], [320, 640, 960], "webp")}
              sizes="(max-width: 768px) 100vw, 50vw"
              width={960}
              height={640}
              alt="Whole-home standby generator installation on concrete pad by Elevation Electrical, LLC licensed contractors providing reliable backup power in Lake Helen Florida"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-xl font-bold mb-3">Backup Generator Solutions</h3>
            <p className="mb-3 text-sm md:text-base">
              Don't let power outages disrupt your life or business. Our generator installation services provide reliable backup power to keep your essential systems running when the grid goes down.
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm md:text-base">Whole home and partial home backup solutions</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm md:text-base">Automatic transfer switch installation</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm md:text-base">Commercial emergency power systems</span>
              </li>
            </ul>
            <Link to="/services/generators" className="btn-primary">
              Explore generator installation
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedServices;
